import { useEffect, type RefObject } from 'react';

/**
 * Keyboard contract for custom dropdown menus (same behavior as
 * AccountMenu / IncidentSwitcher):
 *
 * - Escape closes the menu and returns focus to the trigger (otherwise focus
 *   is dropped on <body> when a menu item had focus).
 * - ArrowDown/ArrowUp move focus through [role="menuitem"] elements inside
 *   `containerRef`, wrapping at the ends.
 * - Home/End jump to the first/last item.
 * - Enter activates the focused item natively.
 *
 * Attach alongside (not instead of) the component's outside-click handling.
 */
export function useMenuKeyboardNav(
  open: boolean,
  containerRef: RefObject<HTMLElement | null>,
  triggerRef: RefObject<HTMLElement | null>,
  onClose: () => void
) {
  useEffect(() => {
    if (!open) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onClose();
        triggerRef.current?.focus();
        return;
      }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Home' || e.key === 'End') {
        const items = Array.from(
          containerRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []
        );
        if (items.length === 0) return;
        e.preventDefault();
        const currentIndex = items.indexOf(document.activeElement as HTMLElement);
        let nextIndex: number;
        if (e.key === 'Home') nextIndex = 0;
        else if (e.key === 'End') nextIndex = items.length - 1;
        else if (e.key === 'ArrowDown') nextIndex = currentIndex < 0 ? 0 : (currentIndex + 1) % items.length;
        else nextIndex = currentIndex < 0 ? items.length - 1 : (currentIndex - 1 + items.length) % items.length;
        items[nextIndex]?.focus();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, containerRef, triggerRef, onClose]);
}
